<template>
  <div class="dashboard">
    <div class="dashboard-header mb-4">
      <h1 class="dashboard-title">
        <i class="fas fa-images mr-2"></i> Saved Images
      </h1>

      <!-- Action buttons only visible when images exist -->
      <b-button-group v-if="!loading && !error && images.length > 0">
        <b-button variant="primary" class="create-button" @click="createNew">
          <i class="fas fa-plus mr-2"></i> Create New Image
        </b-button>
        <b-button variant="outline-secondary" :disabled="refreshing" @click="refreshImages">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
        </b-button>
      </b-button-group>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="text-center my-5 loading-container">
      <div class="spinner-container mb-3">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>
      <p class="loading-text">Loading your saved images...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger error-container">
      <div class="d-flex align-items-center">
        <i class="fas fa-exclamation-triangle mr-3 error-icon"></i>
        <div>
          <h4 class="alert-heading">Error Loading Images</h4>
          <p>{{ error }}</p>
          <b-button variant="primary" class="mt-2" @click="fetchImages">
            <i class="fas fa-sync-alt mr-1"></i> Try Again
          </b-button>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="images.length === 0" class="text-center my-5 empty-container">
      <div class="empty-image-icon mb-3">
        <i class="far fa-images"></i>
      </div>
      <h3 class="mb-3">No Saved Images Yet</h3>
      <p class="text-muted mb-4">Create your first image to get started with overlays and animations.</p>
      <b-button variant="primary" size="lg" class="create-button px-4" @click="createNew">
        <i class="fas fa-plus mr-2"></i> Create New Image
      </b-button>
    </div>

    <!-- Images grid -->
    <div v-else>
      <div class="image-grid">
        <b-card
          v-for="image in images"
          :key="image.id"
          no-body
          class="image-card"
        >
          <!-- Thumbnail with overlay and badges -->
          <div class="image-thumbnail">
            <div class="thumbnail-overlay"></div>
            <img :src="image.thumbnailUrl || image.url" :alt="image.name" class="card-img-top" />

            <!-- Default badge -->
            <div v-if="image.isDefault" class="default-badge">
              <b-badge variant="success" class="default-badge-pill">
                <i class="fas fa-star mr-1"></i> Default
              </b-badge>
            </div>

            <!-- Quick action overlay buttons -->
            <div class="thumbnail-actions">
              <b-button-group size="sm">
                <b-button variant="light" class="action-button" title="Edit Image" @click="editImage(image)">
                  <i class="fas fa-edit"></i>
                </b-button>
                <b-button variant="light" class="action-button" title="Preview Image" @click="previewImage(image)">
                  <i class="fas fa-eye"></i>
                </b-button>
              </b-button-group>
            </div>
          </div>

          <!-- Card body with details -->
          <b-card-body class="py-3">
            <div class="d-flex justify-content-between align-items-start">
              <b-card-title class="image-title mb-1" :title="image.name">
                {{ image.name }}
              </b-card-title>
              <b-dropdown size="sm" right no-caret variant="link" class="p-0 m-0 more-options-dropdown">
                <template v-slot:button-content>
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <b-dropdown-item @click="renameImage(image)">
                  <i class="fas fa-pencil-alt mr-2"></i> Rename
                </b-dropdown-item>
                <b-dropdown-item @click="duplicateImage(image)">
                  <i class="fas fa-copy mr-2"></i> Duplicate
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item variant="danger" @click="confirmDelete(image)">
                  <i class="fas fa-trash-alt mr-2 text-danger"></i>
                  <span class="text-danger">Delete</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <div class="image-metadata">
              <div class="metadata-item" title="Creation Date">
                <i class="far fa-calendar-alt mr-1 text-muted"></i>
                <small class="text-muted">{{ formatDate(image.createdAt) }}</small>
              </div>
            </div>
          </b-card-body>

          <!-- Card footer with actions -->
          <b-card-footer class="py-2">
            <div class="d-flex justify-content-between">
              <b-button variant="outline-primary" size="sm" class="flex-grow-1 mr-2" @click="editImage(image)">
                <i class="fas fa-edit mr-1"></i> Edit
              </b-button>
              <b-button
                variant="outline-success"
                size="sm"
                class="flex-grow-1"
                :disabled="image.isDefault"
                @click="setDefault(image)"
              >
                <template v-if="image.isDefault">
                  <i class="fas fa-check mr-1"></i> Default
                </template>
                <template v-else>
                  <i class="far fa-star mr-1"></i> Set Default
                </template>
              </b-button>
            </div>
          </b-card-footer>
        </b-card>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Confirm Delete"
      header-bg-variant="danger"
      header-text-variant="white"
      ok-variant="danger"
      ok-title="Delete Image"
      @ok="deleteImage"
    >
      <div class="text-center mb-4">
        <i class="fas fa-exclamation-triangle text-danger delete-warning-icon mb-3"></i>
        <h4>Delete Image?</h4>
      </div>

      <p>Are you sure you want to delete <strong>{{ selectedImage ? selectedImage.name : '' }}</strong>?</p>
      <p>This action cannot be undone.</p>

      <div v-if="selectedImage && selectedImage.isDefault" class="alert alert-warning mt-3">
        <i class="fas fa-exclamation-circle mr-2"></i>
        Warning: This is your default image. Deleting it will require you to select a new default.
      </div>
    </b-modal>

    <!-- Rename Modal -->
    <b-modal
      v-model="showRenameModal"
      title="Rename Image"
      ok-title="Save"
      @ok="saveRename"
    >
      <b-form-group
        label="Image Name"
        label-for="image-name-input"
      >
        <b-form-input
          id="image-name-input"
          v-model="newImageName"
          placeholder="Enter a new name for your image"
          required
          autofocus
        ></b-form-input>
      </b-form-group>
    </b-modal>

    <!-- Preview Modal -->
    <b-modal
      v-model="showPreviewModal"
      title="Image Preview"
      size="lg"
      body-class="p-0"
      hide-footer
    >
      <div class="image-preview-container">
        <img v-if="selectedImage" :src="selectedImage.url" class="preview-image" />
      </div>
      <div class="preview-footer p-3 d-flex justify-content-between align-items-center">
        <span v-if="selectedImage" class="image-name">{{ selectedImage.name }}</span>
        <b-button variant="primary" @click="editFromPreview">
          <i class="fas fa-edit mr-1"></i> Edit This Image
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SimpleDashboard',
  props: {
    images: {
      type: Array,
      default: () => []
    },
    onCreateNew: {
      type: Function,
      default: () => {}
    },
    onEditImage: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      localImages: [],
      loading: true,
      refreshing: false,
      error: null,
      showDeleteModal: false,
      showRenameModal: false,
      showPreviewModal: false,
      selectedImage: null,
      newImageName: ''
    };
  },
  created() {
    this.fetchImages();
  },
  methods: {
    async fetchImages() {
      this.loading = true;
      this.error = null;

      try {
        // Try both API endpoints for compatibility
        let response;
        try {
          response = await axios.get('/api/saved-images');
        } catch (firstError) {
          // If the first endpoint fails, try the alternative
          console.log('First endpoint failed, trying alternative');
          response = await axios.get('/businesses/saved-images');
        }

        // Handle both possible response formats
        if (response.data && response.data.images) {
          this.localImages = response.data.images;
        } else if (Array.isArray(response.data)) {
          this.localImages = response.data;
        } else {
          this.localImages = [];
          console.warn('Unexpected response format:', response.data);
        }

        // Emit the updated images
        this.$emit('update:images', this.localImages);
      } catch (err) {
        console.error('Error fetching images:', err);
        this.error = 'Failed to load images. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async refreshImages() {
      if (this.refreshing) return;

      this.refreshing = true;
      try {
        await this.fetchImages();
      } finally {
        this.refreshing = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown date';

      try {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (err) {
        console.error('Date formatting error:', err);
        return 'Invalid date';
      }
    },

    createNew() {
      // Use both the prop and emit the event for flexibility
      this.onCreateNew();
      this.$emit('create-new');
    },

    editImage(image) {
      // Use both the prop and emit the event for flexibility
      this.onEditImage(image);
      this.$emit('edit-image', image);
    },

    previewImage(image) {
      this.selectedImage = image;
      this.showPreviewModal = true;
    },

    editFromPreview() {
      this.showPreviewModal = false;
      if (this.selectedImage) {
        this.editImage(this.selectedImage);
      }
    },

    renameImage(image) {
      this.selectedImage = image;
      this.newImageName = image.name;
      this.showRenameModal = true;
    },

    async saveRename() {
      if (!this.selectedImage || !this.newImageName.trim()) return;

      try {
        await axios.patch(`/api/saved-images/${this.selectedImage.id}`, {
          name: this.newImageName.trim()
        });

        // Update the image in the local state
        this.localImages = this.localImages.map(img => {
          if (img.id === this.selectedImage.id) {
            return { ...img, name: this.newImageName.trim() };
          }
          return img;
        });

        // Also update the selected image
        if (this.selectedImage) {
          this.selectedImage.name = this.newImageName.trim();
        }

        // Emit the updated images
        this.$emit('update:images', this.localImages);
      } catch (err) {
        console.error('Error renaming image:', err);
        alert('Failed to rename image. Please try again.');
      }
    },

    async duplicateImage(image) {
      try {
        // We're creating a copy by downloading the image and creating a new one
        const response = await axios.get(`/api/saved-images/${image.id}/download`);

        if (response.data && response.data.imageData) {
          // Create a new image with the duplicated data
          const duplicateName = `${image.name} (Copy)`;
          await axios.post('/api/saved-images', {
            name: duplicateName,
            imageData: response.data.imageData,
            overlaySettings: image.overlaySettings
          });

          // Refresh the image list
          await this.fetchImages();
        }
      } catch (err) {
        console.error('Error duplicating image:', err);
        alert('Failed to duplicate image. Please try again.');
      }
    },

    async setDefault(image) {
      try {
        await axios.post(`/api/saved-images/${image.id}/set-default`);

        // Update local state
        this.localImages = this.localImages.map(img => ({
          ...img,
          isDefault: img.id === image.id
        }));

        // Emit the updated images
        this.$emit('update:images', this.localImages);
      } catch (err) {
        console.error('Error setting default image:', err);
        alert('Failed to set image as default. Please try again.');
      }
    },

    confirmDelete(image) {
      this.selectedImage = image;
      this.showDeleteModal = true;
    },

    async deleteImage() {
      if (!this.selectedImage) return;

      try {
        await axios.delete(`/api/saved-images/${this.selectedImage.id}`);

        // Remove from local state
        this.localImages = this.localImages.filter(img => img.id !== this.selectedImage.id);

        // Emit the updated images
        this.$emit('update:images', this.localImages);

        // Clear selection
        this.selectedImage = null;
      } catch (err) {
        console.error('Error deleting image:', err);
        alert('Failed to delete image. Please try again.');
      }
    }
  }
};
</script>

<style scoped>
.dashboard {
  padding: 20px;
  background-color: #f9f9f9;
  min-height: 100vh;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eaeaea;
}

.dashboard-title {
  margin: 0;
  color: #333;
  font-size: 1.75rem;
  font-weight: 600;
}

.create-button {
  font-weight: 500;
}

/* Loading state */
.loading-container {
  padding: 3rem;
}

.spinner-container {
  display: flex;
  justify-content: center;
}

.loading-text {
  color: #6c757d;
  font-size: 1.1rem;
}

/* Error state */
.error-container {
  border-left: 5px solid #dc3545;
}

.error-icon {
  font-size: 2rem;
  color: #dc3545;
}

/* Empty state */
.empty-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 3rem 1rem;
}

.empty-image-icon {
  font-size: 5rem;
  color: #dee2e6;
}

/* Image grid */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.image-card {
  position: relative;
  transition: all 0.25s ease;
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  height: 100%;
}

.image-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.image-card:hover .thumbnail-actions {
  opacity: 1;
}

.image-card:hover .thumbnail-overlay {
  opacity: 0.3;
}

/* Thumbnail styling */
.image-thumbnail {
  position: relative;
  height: 180px;
  overflow: hidden;
  background-color: #f0f0f0;
}

.image-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-card:hover .image-thumbnail img {
  transform: scale(1.05);
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #000;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.thumbnail-actions {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.action-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.default-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
}

.default-badge-pill {
  padding: 0.4em 0.75em;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

/* Card body styling */
.image-title {
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.image-metadata {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 5px;
}

.metadata-item {
  display: flex;
  align-items: center;
}

.more-options-dropdown button {
  color: #6c757d;
  width: 30px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.more-options-dropdown button:hover {
  background-color: #f8f9fa;
  color: #495057;
}

/* Delete modal styling */
.delete-warning-icon {
  font-size: 3rem;
  display: block;
}

/* Preview modal styling */
.image-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #212529;
  min-height: 300px;
  max-height: 70vh;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preview-footer {
  border-top: 1px solid #dee2e6;
}

.image-name {
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .dashboard-title {
    margin-bottom: 1rem;
  }

  .image-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }

  .image-thumbnail {
    height: 150px;
  }
}
</style>
